// Post-signup multi-step setup wizard.
// Continues the onboarding visual language from BusinessProfilePage.
// 6 steps: Financial Baseline · Targets · Team · Marketing · Integrations · Compliance.
// Each step saves into customers.company_parameters (JSONB) via /api/onboarding/setup-step.
//
// Skip is always available. Progress bar shows X/6 + percent.
// After step 6 (or "Finish later"), the user is sent to /welcome → dashboard.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import OnboardingLogo from "../../components/onboarding/OnboardingLogo";
import {
  onboardingShell, card, label, inputStyle, primaryBtn, helpText, errorText,
} from "../../components/onboarding/styles";
import { getCurrentCustomer } from "../../lib/customerSession";
import { BACKEND_URL } from "../../lib/backendUrl";

const API_BASE = `${BACKEND_URL}/api`;

const STEPS = [
  { id: "financial_baseline", label: "Financial Baseline", icon: "💰" },
  { id: "targets",            label: "Projections & Targets", icon: "🎯" },
  { id: "team",               label: "Team & Operations", icon: "👥" },
  { id: "marketing",          label: "Marketing & Sales", icon: "📣" },
  { id: "integrations",       label: "Integrations", icon: "🔌" },
  { id: "compliance",         label: "CA & Compliance", icon: "📋" },
];

function Field({ label: lbl, hint, error, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={label}>{lbl}</label>
      {children}
      {hint && !error && <p style={helpText}>{hint}</p>}
      {error && <p style={errorText}>{error}</p>}
    </div>
  );
}

function NumberInput({ value, onChange, placeholder, prefix }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      {prefix && (
        <span style={{
          position: "absolute", left: 12, top: 0, height: 44,
          display: "flex", alignItems: "center", color: "#6B7280",
          fontSize: 14, pointerEvents: "none",
        }}>{prefix}</span>
      )}
      <input
        type="number"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        style={{ ...inputStyle(focused), paddingLeft: prefix ? 32 : 12 }}
      />
    </div>
  );
}

function TextInput({ value, onChange, placeholder, type = "text" }) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      placeholder={placeholder}
      style={inputStyle(focused)}
    />
  );
}

function Pill({ active, onClick, children, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick}
      style={{
        background: active ? "#059669" : "#ffffff",
        color: active ? "white" : "#374151",
        border: `1px solid ${active ? "#059669" : "#D1D5DB"}`,
        borderRadius: 20, padding: "8px 14px",
        fontSize: 12.5, fontWeight: active ? 600 : 500, cursor: "pointer",
      }}>
      {children}
    </button>
  );
}

// ---------- Step views ----------

function FinancialBaselineStep({ values, setValue }) {
  return (
    <>
      <Field label="Current Annual Revenue (ARR)" hint="Total revenue from the last 12 months">
        <NumberInput prefix="₹" placeholder="60,00,000" value={values.arr} onChange={(v) => setValue("arr", v)} />
      </Field>
      <Field label="Current MRR (Monthly Recurring Revenue)" hint="Average monthly revenue right now">
        <NumberInput prefix="₹" placeholder="5,00,000" value={values.mrr} onChange={(v) => setValue("mrr", v)} />
      </Field>
      <Field label="Total Monthly Expenses" hint="Salaries + rent + GST + SaaS + everything else">
        <NumberInput prefix="₹" placeholder="3,00,000" value={values.monthly_expenses} onChange={(v) => setValue("monthly_expenses", v)} />
      </Field>
      <Field label="Outstanding Loans / Liabilities" hint="Total amount you owe to lenders today">
        <NumberInput prefix="₹" placeholder="10,00,000" value={values.outstanding_liabilities} onChange={(v) => setValue("outstanding_liabilities", v)} />
      </Field>
      <Field label="Cash in Hand / Bank Balance" hint="Liquid cash you can deploy this month">
        <NumberInput prefix="₹" placeholder="8,00,000" value={values.cash_in_hand} onChange={(v) => setValue("cash_in_hand", v)} />
      </Field>
    </>
  );
}

function TargetsStep({ values, setValue }) {
  return (
    <>
      <Field label="Revenue Target — Next 6 Months" hint="Conservative near-term goal">
        <NumberInput prefix="₹" placeholder="40,00,000" value={values.revenue_target_6m} onChange={(v) => setValue("revenue_target_6m", v)} />
      </Field>
      <Field label="Revenue Target — Next 12 Months" hint="Annual target. Powers the Trajectory module.">
        <NumberInput prefix="₹" placeholder="1,00,00,000" value={values.revenue_target} onChange={(v) => setValue("revenue_target", v)} />
      </Field>
      <Field label="Expected Growth — % per Month" hint="Average month-over-month growth you're aiming for">
        <NumberInput placeholder="8" value={values.expected_growth_pct_monthly} onChange={(v) => setValue("expected_growth_pct_monthly", v)} />
      </Field>
      <Field label="Planned Fundraising (₹) — if any" hint="Leave blank if not raising">
        <NumberInput prefix="₹" placeholder="0" value={values.planned_fundraise} onChange={(v) => setValue("planned_fundraise", v)} />
      </Field>
    </>
  );
}

function TeamStep({ values, setValue }) {
  return (
    <>
      <Field label="Current Employee Count" hint="Full-time + part-time">
        <NumberInput placeholder="14" value={values.employee_count} onChange={(v) => setValue("employee_count", v)} />
      </Field>
      <Field label="Planned Hiring — Next 6 Months" hint="How many roles do you plan to add?">
        <NumberInput placeholder="3" value={values.planned_hiring_6m} onChange={(v) => setValue("planned_hiring_6m", v)} />
      </Field>
      <Field label="Key Departments" hint="Comma-separated. e.g. Sales, Engineering, Operations, Finance">
        <TextInput placeholder="Sales, Engineering, Operations, Finance" value={values.key_departments} onChange={(v) => setValue("key_departments", v)} />
      </Field>
      <Field label="Monthly Salary Expense" hint="Total monthly payroll cost">
        <NumberInput prefix="₹" placeholder="2,00,000" value={values.monthly_salary_expense} onChange={(v) => setValue("monthly_salary_expense", v)} />
      </Field>
    </>
  );
}

function MarketingStep({ values, setValue }) {
  const channels = ["Google Ads", "Meta Ads", "Cold Outreach", "Referrals", "WhatsApp", "Trade Shows", "SEO", "Other"];
  const selected = Array.isArray(values.primary_sales_channels) ? values.primary_sales_channels : [];
  const toggle = (c) => {
    const next = selected.includes(c) ? selected.filter((x) => x !== c) : [...selected, c];
    setValue("primary_sales_channels", next);
  };
  return (
    <>
      <Field label="Monthly Marketing Budget" hint="Powers your CAC calculation in the CMO module">
        <NumberInput prefix="₹" placeholder="50,000" value={values.marketing_spend} onChange={(v) => setValue("marketing_spend", v)} />
      </Field>
      <Field label="Primary Sales Channels" hint="Pick all that apply">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6 }}>
          {channels.map((c) => (
            <Pill key={c} active={selected.includes(c)} onClick={() => toggle(c)} testid={`channel-${c.replace(/\s+/g, "-").toLowerCase()}`}>
              {c}
            </Pill>
          ))}
        </div>
      </Field>
      <Field label="Current Customer Count">
        <NumberInput placeholder="42" value={values.current_customer_count} onChange={(v) => setValue("current_customer_count", v)} />
      </Field>
      <Field label="Target Customer Count — Next 12 Months">
        <NumberInput placeholder="180" value={values.target_customer_count_12m} onChange={(v) => setValue("target_customer_count_12m", v)} />
      </Field>
    </>
  );
}

function IntegrationsStep({ values, setValue, customerId }) {
  // Mirrors the 5 real providers from /api/integrations/providers
  // (excluding `excel` which has its own upload card on /integrations).
  // All actions here POST to /api/integrations/connect (intent-only) so
  // they show up identically on the /integrations tab + admin checklist.
  const integrations = [
    { id: "tally", name: "Tally Prime", desc: "Live P&L, GST & balance sheet sync via the C-ORE bridge.",
      setup: "Open /integrations → Tally to paste your bridge token after signup. Marking here records your intent." },
    { id: "gst",  name: "GST Portal", desc: "Verify GSTIN and pull GSTR-1 / GSTR-3B summaries.",
      setup: "Confirm your GSTIN in the Compliance step. C-ORE checks it against the GSTN public API." },
    { id: "zoho", name: "Zoho Books & CRM", desc: "Invoices, contacts, deals and pipeline in real time.",
      setup: "OAuth one-click connect — coming soon. Mark intent now, finish from /integrations later." },
    { id: "odoo", name: "Odoo ERP", desc: "Accounting, sales orders, HR/payroll and inventory from Odoo.",
      setup: "Generate an Odoo API key under User Preferences. Paste it on /integrations." },
    { id: "razorpay", name: "Razorpay", desc: "Reconcile payments, settlements and subscriptions.",
      setup: "Razorpay Dashboard → Settings → API Keys. Paste the key id + secret on /integrations." },
  ];
  const connected = values.integrations_connected || {};
  const skipped   = values.integrations_skipped   || {};
  const [busy, setBusy] = useState("");

  const markConnected = async (id) => {
    setBusy(id);
    // Optimistic UI: flip the local flag first so users get instant feedback.
    setValue("integrations_connected", { ...connected, [id]: true });
    if (skipped[id]) setValue("integrations_skipped", { ...skipped, [id]: undefined });
    if (!customerId) { setBusy(""); return; }
    try {
      await fetch(`${API_BASE}/integrations/connect`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customer_id: customerId, provider: id, intent_only: true }),
      });
    } catch (_e) { /* network blip — local flag still flipped */ }
    setBusy("");
  };

  const markUnconnected = async (id) => {
    setBusy(id);
    setValue("integrations_connected", { ...connected, [id]: false });
    if (!customerId) { setBusy(""); return; }
    try {
      await fetch(`${API_BASE}/integrations/disconnect`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customer_id: customerId, provider: id }),
      });
    } catch (_e) { /* ignore */ }
    setBusy("");
  };

  const markSkip = (id) => setValue("integrations_skipped", { ...skipped, [id]: true });

  return (
    <>
      {integrations.map((it) => {
        const isConnected = !!connected[it.id];
        const isSkipped   = !!skipped[it.id];
        const isBusy      = busy === it.id;
        return (
          <div key={it.id} data-testid={`int-${it.id}`} style={{
            border: "1px solid #E5E7EB", borderRadius: 8, padding: 14, marginBottom: 10,
            background: isConnected ? "#F0FDF4" : "#ffffff",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#0F1B2D" }}>{it.name}</div>
                <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>{it.desc}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{
                  padding: "4px 10px", borderRadius: 14, fontSize: 11, fontWeight: 700,
                  background: isConnected ? "#DCFCE7" : isSkipped ? "#FEF3C7" : "#FEE2E2",
                  color:      isConnected ? "#065F46" : isSkipped ? "#92400E" : "#991B1B",
                }}>
                  {isConnected ? "Marked ✅" : isSkipped ? "Skipped — later" : "Not connected ❌"}
                </span>
                <button
                  type="button"
                  data-testid={`int-${it.id}-connect`}
                  disabled={isBusy}
                  onClick={() => (isConnected ? markUnconnected(it.id) : markConnected(it.id))}
                  style={{
                    fontSize: 12, fontWeight: 600,
                    color: isConnected ? "#059669" : "#1E3A5F",
                    background: "transparent", border: "1px solid #D1D5DB", borderRadius: 6,
                    padding: "5px 10px", cursor: isBusy ? "wait" : "pointer",
                    opacity: isBusy ? 0.6 : 1,
                  }}
                >
                  {isBusy ? "Saving…" : isConnected ? "Mark unconnected" : "Mark connected"}
                </button>
                {!isConnected && !isSkipped && (
                  <button type="button" data-testid={`int-${it.id}-skip`} onClick={() => markSkip(it.id)} style={{
                    fontSize: 12, fontWeight: 600, color: "#6B7280",
                    background: "transparent", border: "none", cursor: "pointer", padding: "5px 4px",
                  }}>Skip</button>
                )}
              </div>
            </div>
            {!isConnected && (
              <div style={{ marginTop: 8, fontSize: 11.5, color: "#92400E", background: "#FFFBEB", padding: 8, borderRadius: 6 }}>
                <b>Setup:</b> {it.setup}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

function ComplianceStep({ values, setValue }) {
  return (
    <>
      <Field label="Director(s) Details" hint="Comma-separated. Format: Director Name (8-digit DIN).">
        <TextInput placeholder="Director Name (8-digit DIN)" value={values.directors_details} onChange={(v) => setValue("directors_details", v)} />
      </Field>
      <Field label="Tax Filing Frequency" hint="How often do you file income tax (e.g. quarterly, annually)?">
        <TextInput placeholder="Quarterly advance tax + annual ITR" value={values.tax_filing_frequency} onChange={(v) => setValue("tax_filing_frequency", v)} />
      </Field>
      <Field label="Last ITR Filed Date">
        <TextInput type="date" value={values.last_itr_filed_date} onChange={(v) => setValue("last_itr_filed_date", v)} />
      </Field>
      <Field label="GST Filing Frequency" hint="Monthly (QRMP / regular) or quarterly?">
        <TextInput placeholder="Monthly (regular)" value={values.gst_filing_frequency} onChange={(v) => setValue("gst_filing_frequency", v)} />
      </Field>
    </>
  );
}

// ---------- Page shell ----------

export default function SetupWizardPage() {
  const navigate = useNavigate();
  const [stepIdx, setStepIdx] = useState(0);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState("");

  const customerId = sessionStorage.getItem("onboarding_customer_id");
  const email      = sessionStorage.getItem("onboarding_email");
  // Fallback to the active logged-in customer (e.g. when an existing user
  // clicks "Continue setup" on the dashboard banner — they don't have the
  // onboarding session keys set).
  const activeCustomer = (() => {
    try { return getCurrentCustomer(); } catch (_e) { return null; }
  })();
  const effectiveCustomerId = customerId || activeCustomer?.id || null;
  const effectiveEmail      = email      || activeCustomer?.email || null;

  useEffect(() => {
    // If we have neither, send the user back to the start of the funnel.
    if (!effectiveCustomerId && !effectiveEmail) navigate("/signup", { replace: true });
  }, [effectiveCustomerId, effectiveEmail, navigate]);

  // Hydrate existing values when the wizard mounts.
  useEffect(() => {
    if (!effectiveCustomerId) return;
    fetch(`${API_BASE}/onboarding/setup-progress/${effectiveCustomerId}`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d?.params) setValues((v) => ({ ...d.params, ...v })); })
      .catch(() => {});
  }, [effectiveCustomerId]);

  const setValue = (key, val) => setValues((s) => ({ ...s, [key]: val }));

  const step       = STEPS[stepIdx];
  const isLast     = stepIdx === STEPS.length - 1;
  const completion = useMemo(() => Math.round(((stepIdx + 1) / STEPS.length) * 100), [stepIdx]);

  // Slice out the fields owned by the current step for the save call.
  const stepFieldKeys = useMemo(() => ({
    financial_baseline: ["arr", "mrr", "monthly_expenses", "outstanding_liabilities", "cash_in_hand"],
    targets:            ["revenue_target_6m", "revenue_target", "expected_growth_pct_monthly", "planned_fundraise"],
    team:               ["employee_count", "planned_hiring_6m", "key_departments", "monthly_salary_expense"],
    marketing:          ["marketing_spend", "primary_sales_channels", "current_customer_count", "target_customer_count_12m"],
    integrations:       ["integrations_connected", "integrations_skipped"],
    compliance:         ["directors_details", "tax_filing_frequency", "last_itr_filed_date", "gst_filing_frequency"],
  }), []);

  const collectStepData = () => {
    const keys = stepFieldKeys[step.id];
    const out = {};
    keys.forEach((k) => {
      const v = values[k];
      if (v === undefined || v === "") return;
      // Coerce numeric fields.
      const numericKeys = new Set([
        "arr", "mrr", "monthly_expenses", "outstanding_liabilities", "cash_in_hand",
        "revenue_target_6m", "revenue_target", "expected_growth_pct_monthly", "planned_fundraise",
        "employee_count", "planned_hiring_6m", "monthly_salary_expense",
        "marketing_spend", "current_customer_count", "target_customer_count_12m",
      ]);
      out[k] = numericKeys.has(k) ? Number(v) : v;
    });
    return out;
  };

  const saveAndAdvance = async (kind /* "continue" | "skip" | "finish" */) => {
    setBanner("");
    setSaving(true);
    try {
      const url = kind === "skip"
        ? `${API_BASE}/onboarding/setup-skip`
        : `${API_BASE}/onboarding/setup-step`;
      const payload = {
        customer_id: effectiveCustomerId || undefined,
        email:       effectiveCustomerId ? undefined : effectiveEmail,
        step:        step.id,
        data:        kind === "skip" ? {} : collectStepData(),
        completed:   kind === "finish",
      };
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        setBanner("Couldn't save this step. Please try again.");
        setSaving(false);
        return;
      }
      // Advance or finish.
      if (kind === "finish" || isLast) {
        // Active customers returning from the dashboard banner should go back
        // to the dashboard, not to the post-signup /welcome page.
        navigate(activeCustomer ? "/executive" : "/welcome");
        return;
      }
      setStepIdx((i) => Math.min(STEPS.length - 1, i + 1));
    } catch (_e) {
      setBanner("Network error — please try again.");
    } finally {
      setSaving(false);
    }
  };

  const stepView = (() => {
    switch (step.id) {
      case "financial_baseline": return <FinancialBaselineStep values={values} setValue={setValue} />;
      case "targets":            return <TargetsStep values={values} setValue={setValue} />;
      case "team":               return <TeamStep values={values} setValue={setValue} />;
      case "marketing":          return <MarketingStep values={values} setValue={setValue} />;
      case "integrations":       return <IntegrationsStep values={values} setValue={setValue} customerId={effectiveCustomerId} />;
      case "compliance":         return <ComplianceStep values={values} setValue={setValue} />;
      default: return null;
    }
  })();

  return (
    <div data-testid="setup-wizard-page" style={onboardingShell}>
      <div style={{ marginBottom: 20 }}><OnboardingLogo /></div>

      {/* Progress bar */}
      <div style={{ width: 720, maxWidth: "100%", marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <span style={{ color: "#6B7280", fontSize: 12, fontWeight: 600 }}>
            Step {stepIdx + 2} of {STEPS.length + 1}  · {completion}% complete
          </span>
          <span style={{ color: "#6B7280", fontSize: 12 }}>{step.label}</span>
        </div>
        <div style={{ width: "100%", height: 6, background: "#E5E7EB", borderRadius: 3 }}>
          <div data-testid="wizard-progress-bar"
            style={{ width: `${completion}%`, height: "100%", background: "#059669", borderRadius: 3, transition: "width 0.3s" }} />
        </div>
        {/* Step pills */}
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, gap: 6 }}>
          {STEPS.map((s, i) => (
            <button key={s.id} type="button" data-testid={`wizard-step-${s.id}`} onClick={() => setStepIdx(i)}
              style={{
                flex: 1, padding: "4px 6px", fontSize: 10.5, fontWeight: 600,
                borderRadius: 6, cursor: "pointer", border: "1px solid",
                borderColor: i === stepIdx ? "#059669" : i < stepIdx ? "#A7F3D0" : "#E5E7EB",
                background:  i === stepIdx ? "#DCFCE7" : i < stepIdx ? "#F0FDF4" : "#ffffff",
                color:       i === stepIdx ? "#065F46" : "#374151",
                textAlign: "center",
              }}>
              <div style={{ fontSize: 14 }}>{s.icon}</div>
              <div style={{ marginTop: 1 }}>{s.label}</div>
            </button>
          ))}
        </div>
      </div>

      <div style={{ ...card, width: 720, maxWidth: "100%", padding: 32 }}>
        {banner && (
          <div data-testid="wizard-banner" style={{
            marginBottom: 12, padding: "10px 12px", background: "#FEF2F2",
            border: "1px solid #FECACA", color: "#B91C1C", fontSize: 13, borderRadius: 8,
          }}>{banner}</div>
        )}

        <h2 style={{ color: "#1E3A5F", fontSize: 22, fontWeight: 700, margin: 0 }}>
          {step.icon} {step.label}
        </h2>
        <p style={{ color: "#6B7280", fontSize: 13, marginTop: 6, marginBottom: 20 }}>
          Skip any field that doesn't apply yet — you can edit everything from the dashboard later.
        </p>

        <div data-testid={`wizard-step-body-${step.id}`}>{stepView}</div>

        {/* Footer controls */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginTop: 24 }}>
          <button type="button" data-testid="wizard-back"
            disabled={stepIdx === 0 || saving}
            onClick={() => setStepIdx((i) => Math.max(0, i - 1))}
            style={{
              padding: "10px 16px", fontSize: 13, fontWeight: 600, borderRadius: 8,
              border: "1px solid #D1D5DB", background: "#ffffff", color: "#374151",
              cursor: stepIdx === 0 ? "not-allowed" : "pointer", opacity: stepIdx === 0 ? 0.5 : 1,
            }}>← Back</button>

          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" data-testid="wizard-skip"
              disabled={saving}
              onClick={() => saveAndAdvance("skip")}
              style={{
                padding: "10px 16px", fontSize: 13, fontWeight: 600, borderRadius: 8,
                border: "1px solid #D1D5DB", background: "#ffffff", color: "#6B7280", cursor: "pointer",
              }}>Skip</button>

            <button type="button" data-testid="wizard-finish-later"
              disabled={saving}
              onClick={() => saveAndAdvance("finish")}
              style={{
                padding: "10px 16px", fontSize: 13, fontWeight: 600, borderRadius: 8,
                border: "1px solid #D1D5DB", background: "#ffffff", color: "#6B7280", cursor: "pointer",
              }}>Finish later</button>

            <button type="button" data-testid="wizard-continue"
              disabled={saving}
              onClick={() => saveAndAdvance(isLast ? "finish" : "continue")}
              style={{ ...primaryBtn, width: "auto", padding: "10px 24px", height: 40, opacity: saving ? 0.7 : 1 }}>
              {saving ? "Saving…" : (isLast ? "Complete setup →" : "Save & Continue →")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
